/**
 * Signing certificate refusals (planning P4-3b D-583, D-589, D-593), read as `planning.letters.signing.refusal`: one
 * sentence for each code, and under `details` one for each detail word of a code (a key cannot be both a sentence
 * and a tree). A detail sentence follows its code's sentence.
 */
export default {
  not_home_tenant: 'Signing certificates can be managed and used only by people of this council.',
  signing_certificate_not_found: 'That signing certificate was not found. Reload the list.',
  signing_certificate_not_yours:
    'That personal certificate belongs to someone else. Only the person it names can sign with it.',
  signing_certificate_unusable: 'That certificate cannot sign now.',
  signing_key_unreadable:
    "The certificate's private key could not be opened. The installation's encryption key may have changed. Issue or upload the certificate again.",
  signing_failed: 'gisify could not sign the letter with this certificate.',
  actor_not_permitted: 'You are not permitted to do that with signing certificates.',
  invalid_signing_certificate_request: 'That request is not valid. Check each field and try again.',
  signing_file_too_large: 'The file is too large. A PKCS#12 file or a chain may be at most 64 KiB, and a key 16 KiB.',
  subject_has_ic_number:
    "A name for this certificate holds what looks like an IC number, which a certificate may not carry. Check the names you typed and the council's name on the letterhead.",
  bound_identity_not_found: 'That person is not an active user of this council.',
  letterhead_council_missing:
    "Add the council's name to the letterhead first. The root certificate takes its organization from it.",
  letterhead_council_too_long:
    "The council's name on the letterhead is longer than 64 characters, the most a certificate allows. Shorten it on the letterhead first; it is never cut.",
  letterhead_council_unwritable:
    "The council's name on the letterhead holds a control character a certificate cannot carry. Retype the name on the letterhead first; gisify never changes it for you.",
  signing_root_exists: 'This council already has a root certificate. Delete it before you create another.',
  signing_root_missing: 'Create the council root first, and wait until it is ready.',
  signing_certificates_full: 'The most signing certificates allowed is reached. Delete one you no longer use.',
  import_rate_limited:
    'You have uploaded 10 certificates in the last hour, deleted ones included, which is the most allowed. Try again later.',
  signing_certificate_state:
    'That cannot be done to this certificate now. A certificate being prepared cannot be deleted; wait until it is ready or refused.',
  signing_certificate_name_unclear:
    'This certificate cannot be approved: it does not have exactly one name for gisify to compare with the signatory.',
  pfx_unreadable: 'The PKCS#12 file could not be read.',
  pfx_password_wrong: 'The password does not open this file.',
  pfx_no_integrity:
    'The PKCS#12 file has no password check, so gisify cannot tell that it is whole. Export it again with a password.',
  pem_password_wrong: 'The password does not open this key.',
  key_certificate_mismatch: 'The key does not belong to the certificate in the file.',
  certificate_expired: 'The certificate has expired.',
  chain_too_long: 'The file holds too many certificates. Upload the certificate with a shorter chain.',
  chain_too_large: 'The certificate and its chain are too large to fit in a signature. Upload it with a shorter chain.',
  signing_certificate_duplicate: 'This certificate is already in gisify. Use that one, or delete it first.',
  job_timeout: 'Preparing the certificate took too long and was stopped. Try again.',
  dispatch_failed: 'Preparing the certificate could not be started. Try again.',
  pfx_cipher_unsupported: 'The PKCS#12 file is protected with a method gisify does not accept.',
  pem_unreadable: 'The PEM file could not be read.',
  key_missing: 'The file holds no private key.',
  key_more_than_one: 'The file holds more than one private key. Upload a file with exactly one.',
  key_algorithm:
    'This kind or size of key is not accepted. Use RSA of 2048 to 8192 bits, or ECDSA on P-256, P-384 or P-521.',
  certificate_usage: 'The certificate is not made for signing documents.',
  certificate_extension: 'The certificate carries an extension gisify cannot check.',
  certificate_encoding: 'A certificate in the file is not encoded in the standard way.',
  signing_root_inactive: 'The council root is not ready, so no certificate can be issued under it.',
  signing_root_unreadable:
    "The council root's key could not be opened. The installation's encryption key may have changed. Delete the root and create a new one.",
  generate_failed: 'gisify could not make the certificate.',
  details: {
    signing_certificate_unusable: {
      disabled: 'It is disabled.',
      deleted: 'It was deleted.',
      expired: 'It has expired.',
      not_yet_valid: 'It is not valid yet.',
      root_inactive: 'The council root it was issued under is not ready.',
    },
    signing_failed: {
      key_mismatch: 'The stored key does not belong to the certificate.',
      sign_error: 'Signing stopped with an error. Try again, and tell an administrator if it happens again.',
      certificate_changed: 'The signature names a different certificate from the one chosen.',
      too_large: 'The certificate and its chain are too large to fit in a signature.',
    },
    pfx_unreadable: {
      ber: 'Its structure is damaged or cut short.',
      shape: 'It holds parts gisify does not read.',
      too_complex: 'It is too large or too deeply nested.',
      iterations: 'Its password is stretched more times than gisify allows.',
    },
    pfx_cipher_unsupported: {
      rc2_40: 'It uses 40-bit RC2. Export it again with AES-256-CBC, the default of OpenSSL 3.',
      rc2_128: 'It uses 128-bit RC2. Export it again with AES-256-CBC.',
      rc4_128: 'It uses 128-bit RC4. Export it again with AES-256-CBC.',
      rc4_40: 'It uses 40-bit RC4. Export it again with AES-256-CBC.',
      des2: 'It uses two-key Triple DES. Export it again with AES-256-CBC.',
      pbes1: 'It uses an old PBES1 method. Export it again with AES-256-CBC.',
      pbes2_cipher: 'Its cipher is not AES-CBC; AES-GCM is not accepted either. Export it again with AES-256-CBC.',
      pbmac1: 'Its password check is PBMAC1. Export it again with an HMAC-SHA-256 check.',
      enveloped: 'It is locked to a certificate, not to a password. Export it again with a password.',
      unknown: 'gisify does not know its method. Export it again with AES-256-CBC.',
    },
    pem_unreadable: {
      armour: 'Its BEGIN and END lines are missing or damaged.',
      legacy_encryption: 'The key uses the old OpenSSL encryption. Convert it to PKCS#8 with openssl pkcs8 -topk8.',
      not_a_key: 'The key block does not hold a private key.',
      two_keys: 'It holds more than one key.',
      no_certificate: 'The chain file holds no certificate.',
    },
    key_algorithm: {
      rsa_small: 'The RSA key is shorter than 2048 bits.',
      rsa_large: 'The RSA key is longer than 8192 bits.',
      curve: 'The elliptic curve is not P-256, P-384 or P-521.',
      rsa_pss: 'The key may be used for RSA-PSS only.',
      ed25519: 'The key is Ed25519.',
      other: 'The key is of another kind, or its values are not accepted.',
    },
    certificate_usage: {
      is_ca: 'It is a CA certificate, which cannot sign letters.',
      key_usage: 'Its key usage does not allow digital signatures.',
      extended_key_usage: 'Its extended key usage does not allow signing documents.',
    },
    certificate_extension: {
      duplicate: 'It repeats an extension.',
      critical_unknown: 'It has a critical extension gisify does not know.',
      constraints: 'It carries name or policy constraints gisify does not enforce.',
    },
    certificate_encoding: {
      leaf: 'The certificate itself is not in strict DER.',
      chain: 'A certificate of the chain is not in strict DER.',
    },
    generate_failed: {
      internal: 'Something went wrong inside gisify. Try again, and tell an administrator if it happens again.',
    },
  },
}
